import { Calendar, Clock, Dumbbell, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { formatExerciseValue, getExerciseUnitLabel } from '../../utils/exerciseFormatters';
import { formatRecoveryDisplaySeconds } from '../../utils/formatRecoveryTime';
import { formatWorkoutRepsTarget } from '../../constants/workoutRepsMode';
import { formatWorkoutWeightTarget } from '../../constants/workoutSeriesPlan';
import { getWorkoutSetTypeLabel, getWorkoutSetTypeBadgeClass } from '../../constants/workoutSetTypes';

function parseTrainerNote(note) {
    if (!note) return null;
    if (typeof note === 'string' && note.trim().startsWith('{')) {
        try {
            const notesObj = JSON.parse(note);
            return notesObj.trainer || notesObj.user || notesObj.system || null;
        } catch {
            return note.trim();
        }
    }
    if (typeof note === 'object' && note !== null) {
        return note.trainer || note.user || note.system || null;
    }
    return note.trim() || null;
}

function getExerciseTargetDisplay(esercizio) {
    if (
        esercizio.set_type === 'piramidale' ||
        esercizio.reps_mode === 'range' ||
        esercizio.reps_mode === 'amrap'
    ) {
        const repsTarget = formatWorkoutRepsTarget(esercizio);
        if (repsTarget) return repsTarget;
    }
    return formatExerciseValue(esercizio.ripetizioni, esercizio);
}

function ExerciseThumbnail({ esercizio }) {
    if (!esercizio.immagine_nome) {
        return (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                <Dumbbell size={16} className="text-gray-400 dark:text-white/40" aria-hidden />
            </span>
        );
    }

    return (
        <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
            <img
                src={`/api/serve_image.php?filename=${encodeURIComponent(esercizio.immagine_nome)}`}
                alt=""
                className="h-full w-full object-cover select-none"
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
                onError={(e) => {
                    e.currentTarget.style.display = 'none';
                }}
            />
        </span>
    );
}

function WorkoutExerciseRow({ esercizio, index, t, showThumbnail }) {
    const displayValue = getExerciseTargetDisplay(esercizio);
    const displayLabel = getExerciseUnitLabel(esercizio);
    const capitalizedLabel = displayLabel.charAt(0).toUpperCase() + displayLabel.slice(1);
    const trainerNote = parseTrainerNote(esercizio.note);
    const recovery = formatRecoveryDisplaySeconds(esercizio.tempo_recupero);
    const weight = formatWorkoutWeightTarget(esercizio);

    return (
        <li className="px-3 py-2.5 sm:px-4">
            <div className="flex items-start gap-2.5 sm:gap-3">
                {showThumbnail ? <ExerciseThumbnail esercizio={esercizio} /> : null}
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                {index + 1}. {esercizio.nome}
                            </p>
                            {esercizio.gruppo_muscolare ? (
                                <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-white/55">
                                    {esercizio.gruppo_muscolare}
                                </p>
                            ) : null}
                        </div>
                        <div className="flex shrink-0 flex-wrap justify-end gap-1">
                            {esercizio.set_type && esercizio.set_type !== 'normal' ? (
                                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${getWorkoutSetTypeBadgeClass(esercizio.set_type)}`}>
                                    {getWorkoutSetTypeLabel(esercizio.set_type)}
                                </span>
                            ) : null}
                            {esercizio.linked_to_previous == 1 ? (
                                <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                                    {t('gym.membersManager.workouts.detail.linkedBadge')}
                                </span>
                            ) : null}
                        </div>
                    </div>

                    <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-600 dark:text-white/70">
                        <span>
                            <span className="text-gray-400 dark:text-white/45">{t('gym.membersManager.workouts.detail.series')}</span>{' '}
                            <span className="font-medium text-gray-900 dark:text-white">{esercizio.serie}</span>
                        </span>
                        <span className="text-gray-300 dark:text-white/20" aria-hidden>·</span>
                        <span>
                            <span className="text-gray-400 dark:text-white/45">{capitalizedLabel}</span>{' '}
                            <span className="font-medium text-gray-900 dark:text-white">{displayValue}</span>
                        </span>
                        <span className="text-gray-300 dark:text-white/20" aria-hidden>·</span>
                        <span>
                            <span className="text-gray-400 dark:text-white/45">{t('gym.membersManager.workouts.detail.weight')}</span>{' '}
                            <span className="font-medium text-gray-900 dark:text-white">{weight}</span>
                        </span>
                        {recovery ? (
                            <>
                                <span className="text-gray-300 dark:text-white/20" aria-hidden>·</span>
                                <span className="inline-flex items-center gap-1">
                                    <Clock size={11} className="text-gray-400 dark:text-white/45" aria-hidden />
                                    <span className="font-medium text-gray-900 dark:text-white">{recovery}</span>
                                </span>
                            </>
                        ) : null}
                    </p>

                    {trainerNote ? (
                        <p className="mt-1.5 rounded-md border border-amber-200/80 bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-100">
                            <span className="font-semibold">{t('gym.membersManager.workouts.detail.note')}</span> {trainerNote}
                        </p>
                    ) : null}
                </div>
            </div>
        </li>
    );
}

export default function WorkoutDetailModal({ workout, client, isOpen, onClose }) {
    const { t, i18n } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 639px)');
    const dateLocale = i18n.language?.startsWith('it') ? 'it-IT' : undefined;

    if (!isOpen || !workout) return null;

    const exercises = workout.esercizi || [];
    const createdLabel = workout.data_creazione
        ? new Date(workout.data_creazione).toLocaleDateString(dateLocale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        })
        : null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 backdrop-blur-sm sm:p-4">
            <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900">
                <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-white/10">
                    <div className="min-w-0">
                        <h2 className="truncate text-base font-bold text-gray-900 dark:text-white sm:text-lg">
                            {workout.nome}
                        </h2>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-white/65 sm:text-sm">
                            {t('gym.membersManager.workouts.detail.client', { name: client?.name || '-' })}
                        </p>
                        {workout.descrizione ? (
                            <p className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-white/55 sm:text-sm">
                                <span className="font-medium text-gray-600 dark:text-white/70">
                                    {t('gym.membersManager.workoutModal.obiettivo')}:
                                </span>{' '}
                                {workout.descrizione}
                            </p>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 dark:text-white/80 dark:hover:bg-white/10"
                        aria-label={t('gym.membersManager.workouts.detail.close')}
                    >
                        <X size={18} aria-hidden />
                    </button>
                </div>

                <div className="shrink-0 border-b border-gray-100 px-3 py-2 dark:border-white/5 sm:px-4">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-white/60">
                        <span className="font-medium text-gray-700 dark:text-white/80">
                            {t('gym.membersManager.workouts.exercisesCount', { count: exercises.length })}
                        </span>
                        {createdLabel ? (
                            <>
                                <span className="text-gray-300 dark:text-white/20" aria-hidden>·</span>
                                <span className="inline-flex items-center gap-1">
                                    <Calendar size={12} aria-hidden />
                                    {t('gym.membersManager.workouts.detail.created', { date: createdLabel })}
                                </span>
                            </>
                        ) : null}
                    </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto">
                    {exercises.length > 0 ? (
                        <ul className="divide-y divide-gray-100 dark:divide-white/5">
                            {exercises.map((esercizio, index) => (
                                <WorkoutExerciseRow
                                    key={esercizio.id ?? `${esercizio.nome}-${index}`}
                                    esercizio={esercizio}
                                    index={index}
                                    t={t}
                                    showThumbnail={!isMobile}
                                />
                            ))}
                        </ul>
                    ) : (
                        <div className="px-4 py-10 text-center">
                            <Dumbbell size={40} className="mx-auto mb-2 text-gray-300 dark:text-white/30" aria-hidden />
                            <p className="text-sm text-gray-500 dark:text-white/60">
                                {t('gym.membersManager.workouts.detail.noExercises')}
                            </p>
                        </div>
                    )}
                </div>

                <div className="shrink-0 border-t border-gray-200 p-3 dark:border-white/10">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full rounded-lg bg-indigo-600 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
                    >
                        {t('gym.membersManager.workouts.detail.close')}
                    </button>
                </div>
            </div>
        </div>
    );
}
